import { useState } from 'react'
import { useCreateTeamRule, useFields, useTeamRules } from '@/features/admin/access/hooks'
import { TeamRuleEditor } from './TeamRuleEditor'
import { formatError } from './formatError'

const OBJECT_TYPES = [
  { code: 'contract', label: 'Umowy' },
]

export function TeamVisibilityPanel({ teamId }: { teamId: number }) {
  const [activeType, setActiveType] = useState(OBJECT_TYPES[0].code)
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2 border-b">
        {OBJECT_TYPES.map(t => (
          <button
            key={t.code}
            type="button"
            onClick={() => setActiveType(t.code)}
            className={`text-sm px-3 py-1.5 border-b-2 ${
              activeType === t.code ? 'border-sky-600 text-sky-700 font-medium' : 'border-transparent text-slate-600'
            }`}
          >{t.label}</button>
        ))}
      </div>
      <ObjectTeamVisibility teamId={teamId} objectType={activeType} />
    </div>
  )
}

function ObjectTeamVisibility({ teamId, objectType }: { teamId: number; objectType: string }) {
  const fieldsQ = useFields(objectType)
  const rulesQ = useTeamRules(teamId, objectType)
  const create = useCreateTeamRule(teamId, objectType)
  const [error, setError] = useState<string | null>(null)

  const addRule = () => {
    setError(null)
    create.mutate(
      {
        name: `Reguła ${(rulesQ.data?.length ?? 0) + 1}`,
        predicate: { type: 'true' },
      },
      { onError: e => setError(formatError(e)) },
    )
  }

  if (fieldsQ.isLoading || rulesQ.isLoading) return <p className="text-xs text-slate-500">Ładowanie…</p>
  if (!fieldsQ.data || !rulesQ.data) return null

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Reguły w jednym zespole są łączone <strong>I</strong>.
          {' '}Reguły z różnych zespołów łączone <strong>LUB</strong> (multi-team = szerszy widok).
          {' '}Reguły zespołu × reguły roli: <strong>I</strong> (intersekcja).
          {' '}Brak reguł zespołu = brak ograniczenia z tego wymiaru.
        </p>
        <button
          type="button"
          onClick={addRule}
          disabled={create.isPending}
          className="text-xs bg-sky-600 text-white rounded px-3 py-1.5 disabled:opacity-50 whitespace-nowrap"
        >+ Nowa reguła</button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {rulesQ.data.length === 0 && (
        <p className="text-sm text-slate-500 italic py-4">
          Brak reguł zespołu - członkowie nie mają tu dodatkowych ograniczeń (widoczność wynika tylko z ich ról).
        </p>
      )}

      <div className="flex flex-col gap-3">
        {rulesQ.data.map(rule => (
          <TeamRuleEditor
            key={rule.id}
            teamId={teamId}
            objectType={objectType}
            rule={rule}
            metadata={fieldsQ.data}
          />
        ))}
      </div>
    </div>
  )
}
